import React, {useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {getLanguageCode} from 'modules/meta/selectors'
import {useTranslateQuery} from 'modules/translationApi'
import {getIsTranslationAvailable} from 'utils/translations'

import {Box} from '@vio/atlas/helpers/Box'
import {TruncatedContent} from '@vio/atlas/helpers/TruncatedContent'
import {
  ReviewComment,
  ReviewCommentProps
} from '@vio/shared/src/search/ReviewComment'

import {ReviewText} from './ReviewText'
import {ReviewTranslation} from './ReviewTranslation'

export type TranslationState = 'original' | 'translating' | 'translated'

interface Props extends ReviewCommentProps {
  reviewText: string
  reviewLanguage: string
  truncatedContentLines?: number
  disableReadMore?: boolean
  searchKeywords?: string[]
  translatedReviewText?: string
}
export const ReviewCommentWrapper: React.FC<Props> = ({
  reviewText,
  reviewLanguage,
  truncatedContentLines = 5,
  disableReadMore = false,
  searchKeywords,
  translatedReviewText,
  ...props
}) => {
  const languageCode = useSelector(getLanguageCode)

  const [shouldTranslate, setShouldTranslate] = useState(false)
  const [showOriginal, setShowOriginal] = useState(false)

  const isTranslationAvailable = useMemo(
    () => getIsTranslationAvailable(reviewLanguage, languageCode),
    [reviewLanguage, languageCode]
  )

  const {
    data: translatedText,
    isLoading,
    isSuccess
  } = useTranslateQuery(
    {text: reviewText, targetLanguage: languageCode},
    {
      skip: !shouldTranslate
    }
  )

  const translationState: TranslationState = useMemo(() => {
    if (showOriginal) return 'original'
    if (isLoading) return 'translating'
    if (isSuccess || translatedReviewText) return 'translated'
    return 'original'
  }, [showOriginal, isLoading, isSuccess, translatedReviewText])

  const onTranslateReview = () => {
    setShowOriginal(false)

    if (!shouldTranslate && !translatedReviewText) {
      setShouldTranslate(true)
    }
  }

  return (
    <div>
      <ReviewComment {...props} languageCode={languageCode}>
        <TruncatedContent
          onReadMoreClick={disableReadMore ? () => null : undefined}
          lines={truncatedContentLines}
        >
          <ReviewText
            translationState={translationState}
            translatedText={
              translatedReviewText ?? (translatedText as string) ?? ''
            }
            originalText={reviewText}
            searchKeywords={searchKeywords}
          />
        </TruncatedContent>
      </ReviewComment>
      {isTranslationAvailable && (
        <Box marginTop="s400">
          <ReviewTranslation
            translationState={translationState}
            onTranslate={onTranslateReview}
            onShowOriginal={() => setShowOriginal(true)}
          />
        </Box>
      )}
    </div>
  )
}
